import React, { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/utils/cn';

interface InteractiveThresholdSliderProps {
  currentQuality: number; // 0-1
  targetThreshold: number; // 0-1
  fallbackThreshold: number; // 0-1
  onThresholdChange: (target: number, fallback: number) => Promise<void>;
  className?: string;
  disabled?: boolean;
}

const InteractiveThresholdSlider: React.FC<InteractiveThresholdSliderProps> = ({
  currentQuality,
  targetThreshold,
  fallbackThreshold,
  onThresholdChange,
  className,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState<'target' | 'fallback' | null>(null);
  const [localTarget, setLocalTarget] = useState(targetThreshold);
  const [localFallback, setLocalFallback] = useState(fallbackThreshold);
  const [isUpdating, setIsUpdating] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Update local state when props change
  useEffect(() => {
    if (!isDragging) {
      setLocalTarget(targetThreshold);
      setLocalFallback(fallbackThreshold);
    }
  }, [targetThreshold, fallbackThreshold, isDragging]);


  const getSliderPosition = useCallback((value: number) => {
    return Math.max(0, Math.min(100, value * 100));
  }, []);

  const getValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(1, position));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent, type: 'target' | 'fallback') => {
    if (disabled || isUpdating) return;
    e.preventDefault();
    setIsDragging(type);
  }, [disabled, isUpdating]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const newValue = getValueFromPosition(e.clientX);

    if (isDragging === 'target') {
      // Target cannot go below fallback
      const constrainedTarget = Math.max(localFallback, newValue);
      setLocalTarget(constrainedTarget);
    } else if (isDragging === 'fallback') {
      // Fallback cannot go above target (min cannot be dragged above target)
      const constrainedFallback = Math.min(localTarget, newValue);
      setLocalFallback(constrainedFallback);
    }
  }, [isDragging, localFallback, localTarget, getValueFromPosition]);

  const handleMouseUp = useCallback(async () => {
    if (!isDragging) return;

    setIsDragging(null);
    
    // Only update if values actually changed
    if (localTarget !== targetThreshold || localFallback !== fallbackThreshold) {
      setIsUpdating(true);
      try {
        await onThresholdChange(localTarget, localFallback);
      } catch (error) {
        console.error('Failed to update thresholds:', error);
        // Reset to original values on error
        setLocalTarget(targetThreshold);
        setLocalFallback(fallbackThreshold);
      } finally {
        setIsUpdating(false);
      }
    }
  }, [isDragging, localTarget, localFallback, targetThreshold, fallbackThreshold, onThresholdChange]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const isSplitSlider = Math.abs(localTarget - localFallback) < 0.01;
  const currentQualityPercent = getSliderPosition(currentQuality);
  const targetPercent = getSliderPosition(localTarget);
  const fallbackPercent = getSliderPosition(localFallback);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Quality Bar with Gradient */}
      <div className="relative">
        <div className="text-sm font-medium text-white/90 mb-2 flex items-center justify-between">
          <span>Overall Quality</span>
          <span className="text-xl font-mono">
            {Math.round(currentQuality * 100)}%
          </span>
        </div>
        
        {/* Main Quality Bar */}
        <div 
          ref={sliderRef}
          className={cn(
            "relative h-8 rounded-lg border border-white/20 cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          style={{
            background: `linear-gradient(90deg, 
              #ef4444 0%, 
              #ef4444 ${fallbackPercent}%, 
              #f59e0b ${fallbackPercent}%, 
              #f59e0b ${targetPercent}%, 
              #10b981 ${targetPercent}%, 
              #10b981 100%
            )`
          }}
        >
          {/* Current Quality Indicator */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg rounded-full z-20"
            style={{ left: `${currentQualityPercent}%` }}
          />
          
          {/* Threshold Sliders */}
          {!isSplitSlider ? (
            <>
              {/* Fallback Threshold Slider */}
              <div
                className={cn(
                  "absolute top-1/2 w-4 h-6 bg-amber-400 border-2 border-white rounded cursor-grab transform -translate-y-1/2 -translate-x-2 shadow-lg z-30",
                  isDragging === 'fallback' && "cursor-grabbing scale-110",
                  disabled && "cursor-not-allowed"
                )}
                style={{ left: `${fallbackPercent}%` }}
                onMouseDown={(e) => handleMouseDown(e, 'fallback')}
                title={`Minimum: ${Math.round(localFallback * 100)}%`}
              />
              
              {/* Target Threshold Slider */}
              <div
                className={cn(
                  "absolute top-1/2 w-4 h-6 bg-emerald-400 border-2 border-white rounded cursor-grab transform -translate-y-1/2 -translate-x-2 shadow-lg z-30",
                  isDragging === 'target' && "cursor-grabbing scale-110",
                  disabled && "cursor-not-allowed"
                )}
                style={{ left: `${targetPercent}%` }}
                onMouseDown={(e) => handleMouseDown(e, 'target')}
                title={`Target: ${Math.round(localTarget * 100)}%`}
              />
            </>
          ) : (
            /* Split Slider when target = fallback */
            <div
              className="absolute top-1/2 w-4 h-8 transform -translate-y-1/2 -translate-x-2 shadow-lg z-30"
              style={{ left: `${targetPercent}%` }}
            >
              {/* Top half - Target */}
              <div
                className={cn(
                  "w-full h-1/2 bg-emerald-400 border-2 border-white rounded-t cursor-grab",
                  isDragging === 'target' && "cursor-grabbing bg-emerald-300",
                  disabled && "cursor-not-allowed"
                )}
                onMouseDown={(e) => handleMouseDown(e, 'target')}
                title={`Target: ${Math.round(localTarget * 100)}%`}
              />
              {/* Bottom half - Fallback */}
              <div
                className={cn(
                  "w-full h-1/2 bg-amber-400 border-2 border-white border-t-0 rounded-b cursor-grab",
                  isDragging === 'fallback' && "cursor-grabbing bg-amber-300",
                  disabled && "cursor-not-allowed"
                )}
                onMouseDown={(e) => handleMouseDown(e, 'fallback')}
                title={`Minimum: ${Math.round(localFallback * 100)}%`}
              />
            </div>
          )}
          
          {/* Loading indicator */}
          {isUpdating && (
            <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        
        {/* Threshold Labels */}
        <div className="flex justify-between text-xs text-white/60 mt-1">
          <span>0%</span>
          <div className="flex gap-4">
            <span className="text-amber-400">
              Min: {Math.round(localFallback * 100)}%
            </span>
            <span className="text-emerald-400">
              Target: {Math.round(localTarget * 100)}%
            </span>
          </div>
          <span>100%</span>
        </div>
      </div>

      {/* Warning Message */}
      {currentQuality < localFallback && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <span className="w-4 h-4 flex items-center justify-center">⚠</span>
          <span>Below Minimum Threshold</span>
        </div>
      )}
      
      {currentQuality >= localFallback && currentQuality < localTarget && (
        <div className="flex items-center gap-2 text-amber-400 text-sm">
          <span className="w-4 h-4 flex items-center justify-center">⚡</span>
          <span>Acceptable Quality - Consider Improving</span>
        </div>
      )}
      
      {currentQuality >= localTarget && (
        <div className="flex items-center gap-2 text-emerald-400 text-sm">
          <span className="w-4 h-4 flex items-center justify-center">✓</span>
          <span>Target Quality Achieved</span>
        </div>
      )}
    </div>
  );
};

export default InteractiveThresholdSlider;